import { useLazyQuery, useMutation } from "@apollo/client";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { clientNeoFact } from "@neonumy/api-clients";
import { FormInstance } from "antd";
import {
  CREATE_CLIENT_VAT_ACCOUNTING_CONFIG,
  CREATE_PRODUCT_ACCOUNTING_CONFIG,
  DELETE_CLIENT_VAT_ACCOUNTING_CONFIG,
  DELETE_PRODUCT_ACCOUNTING_CONFIG,
  GET_CLIENT_VAT_ACCOUNTING_CONFIG,
  GET_PRODUCT_ACCOUNTING_CONFIG,
  LIST_CLIENT_VAT_ACCOUNTING_CONFIGS,
  LIST_PRODUCT_ACCOUNTING_CONFIG,
  UPDATE_CLIENT_VAT_ACCOUNTING_CONFIG,
  UPDATE_PRODUCT_ACCOUNTING_CONFIG,
} from "queries/neoFact/sales";
import { useEffect } from "react";

export default function useSales(id?: ID, form?: FormInstance, hasNotAccountingFirm?: boolean) {
  const [
    listClientVatAccountingConfigs,
    {
      refetch: refetchListClientVatAccountingConfigs,
      loading: loadingListClientVatAccountingConfigs,
      data: listClientVatAccountingConfigsData,
      error: listClientVatAccountingConfigsError,
    },
  ] = useLazyQuery<ListClientVatAccountingConfigsResponse, ListClientVatAccountingConfigsVars>(
    LIST_CLIENT_VAT_ACCOUNTING_CONFIGS,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (listClientVatAccountingConfigsError) onErrorGraphql(listClientVatAccountingConfigsError);
  }, [listClientVatAccountingConfigsError]);

  const clientsVatAccountingData: ListClientVatAccountingConfigsResults =
    listClientVatAccountingConfigsData?.listClientVatAccountingConfigs?.results?.map(item => ({
      ...item,
      key: item.id,
    })) ?? [];

  const clientVatAccountingTotalCount =
    listClientVatAccountingConfigsData?.listClientVatAccountingConfigs?.totalCount ?? 0;

  const [createUpdateClientVatAccounting, { loading: createUpdateClientVatAccountingLoading }] =
    useMutation<
      CreateUpdateClientVatAccountingConfigMutationResponse,
      CreateClientVatAccountingConfigVars | UpdateClientVatAccountingConfigVars
    >(id ? UPDATE_CLIENT_VAT_ACCOUNTING_CONFIG : CREATE_CLIENT_VAT_ACCOUNTING_CONFIG, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  const [deleteClientVatAccountingConfig] = useMutation<
    DeleteClientVatAccountingConfigResponse,
    DeleteClientVatAccountingConfigVars
  >(DELETE_CLIENT_VAT_ACCOUNTING_CONFIG, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [
    getClientVatAccountingConfig,
    {
      refetch: refetchGetClientVatAccountingConfig,
      data: getClientVatAccountingConfigData,
      error: getClientVatAccountingConfigError,
    },
  ] = useLazyQuery<GetClientVatAccountingConfigResponse, GetVarTypeID>(
    GET_CLIENT_VAT_ACCOUNTING_CONFIG,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (getClientVatAccountingConfigError) onErrorGraphql(getClientVatAccountingConfigError);
  }, [getClientVatAccountingConfigError]);

  useEffect(() => {
    const config = getClientVatAccountingConfigData?.getClientVatAccountingConfig;
    if (!config) return;

    form?.setFields([
      {
        name: "clientVatType",
        value: {
          label: `${config?.clientVatType?.code} - ${config?.clientVatType?.name}`,
          value: config?.clientVatType?.id,
        },
      },
      {
        name: "productType",
        value: {
          label: `${config?.productType?.code} - ${config?.productType?.name}`,
          value: config?.productType?.id,
        },
      },
      {
        name: "counterpartCode",
        value: hasNotAccountingFirm
          ? config?.counterpartCode
          : config?.counterpartCodeData && {
              label: `${config?.counterpartCodeData?.code} - ${config?.counterpartCodeData?.name}`,
              value: config?.counterpartCodeData?.code,
            },
      },
    ]);
  }, [getClientVatAccountingConfigData, form, hasNotAccountingFirm]);

  const [
    listProductAccountingConfig,
    {
      refetch: refetchListProductAccountingConfig,
      loading: loadingListProductAccountingConfig,
      data: listProductAccountingConfigData,
      error: listProductAccountingConfigError,
    },
  ] = useLazyQuery<ListProductAccountingConfigResponse, ListProductAccountingConfigVars>(
    LIST_PRODUCT_ACCOUNTING_CONFIG,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (listProductAccountingConfigError) onErrorGraphql(listProductAccountingConfigError);
  }, [listProductAccountingConfigError]);

  const productsAccountingData: ListProductAccountingConfigResults =
    listProductAccountingConfigData?.listProductAccountingConfig?.results?.map(item => ({
      ...item,
      key: item.id,
    })) ?? [];

  const productsAccountingTotalCount =
    listProductAccountingConfigData?.listProductAccountingConfig?.totalCount ?? 0;

  const [createUpdateProductAccounting, { loading: createUpdateProductAccountingLoading }] =
    useMutation<
      CreateUpdateproductAccountingConfigMutationResponse,
      CreateProductAccountingConfigVars | UpdateProductAccountingConfigVars
    >(id ? UPDATE_PRODUCT_ACCOUNTING_CONFIG : CREATE_PRODUCT_ACCOUNTING_CONFIG, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  const [deleteProductAccountingConfig] = useMutation<
    DeleteProductAccountingConfigResponse,
    DeleteProductAccountingConfigVars
  >(DELETE_PRODUCT_ACCOUNTING_CONFIG, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [
    getProductAccountingConfig,
    {
      refetch: refetchGetProductAccountingConfig,
      data: getProductAccountingConfigData,
      error: getProductAccountingConfigError,
    },
  ] = useLazyQuery<GetProductAccountingConfigResponse, GetVarTypeID>(
    GET_PRODUCT_ACCOUNTING_CONFIG,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (getProductAccountingConfigError) onErrorGraphql(getProductAccountingConfigError);
  }, [getProductAccountingConfigError]);

  useEffect(() => {
    const config = getProductAccountingConfigData?.getProductAccountingConfig;
    if (!config) return;

    form?.setFields([
      {
        name: "clientVatType",
        value: {
          label: `${config?.clientVatType?.code} - ${config?.clientVatType?.name}`,
          value: config?.clientVatType?.id,
        },
      },
      {
        name: "productType",
        value: {
          label: `${config?.productType?.code} - ${config?.productType?.name}`,
          value: config?.productType?.id,
        },
      },
      {
        name: "vat",
        value: {
          label: `${config?.vat?.code} - ${config?.vat?.name} - ${config?.vat?.ratePercentage} %`,
          value: config?.vat?.id,
        },
      },
      {
        name: "counterpartCode",
        value: hasNotAccountingFirm
          ? config?.counterpartCode
          : config?.counterpartCodeData && {
              label: `${config?.counterpartCodeData?.code} - ${config?.counterpartCodeData?.name}`,
              value: config?.counterpartCodeData?.code,
            },
      },
    ]);
  }, [getProductAccountingConfigData, form, hasNotAccountingFirm]);

  return {
    productsAccountingData,
    clientsVatAccountingData,
    getProductAccountingConfig,
    listProductAccountingConfig,
    productsAccountingTotalCount,
    getClientVatAccountingConfig,
    createUpdateProductAccounting,
    deleteProductAccountingConfig,
    clientVatAccountingTotalCount,
    listClientVatAccountingConfigs,
    createUpdateClientVatAccounting,
    deleteClientVatAccountingConfig,
    refetchGetProductAccountingConfig,
    loadingListProductAccountingConfig,
    refetchListProductAccountingConfig,
    refetchGetClientVatAccountingConfig,
    createUpdateProductAccountingLoading,
    refetchListClientVatAccountingConfigs,
    loadingListClientVatAccountingConfigs,
    createUpdateClientVatAccountingLoading,
  };
}
